import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

const PROBLEMS = [
  "Knowledge scattered across laptops",
  "Team can't access what they need",
  "Every document starts from scratch",
  "Hours wasted recreating existing work",
  "Knowledge walks out the door",
];

export const Scene04Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.gentle });

  const problemSprings = PROBLEMS.map((_, i) =>
    spring({ frame: frame - i * 20, fps, config: SPRING.snappy }),
  );

  // "The Gap" section appears from right at frame 150
  const gapAppear = spring({ frame: frame - 150, fps, config: SPRING.gentle });
  const competitorAppear = spring({ frame: frame - 165, fps, config: SPRING.snappy });
  const smeAppear = spring({ frame: frame - 180, fps, config: SPRING.punch });

  // Background red pulse
  const bgPulse = interpolate(Math.sin(frame * 0.04), [-1, 1], [0.02, 0.05]);

  // Exit fade
  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Cascade diagonal offset constants
  const CASCADE_LEFT = 80;
  const CASCADE_TOP_START = 155;
  const CASCADE_STEP_H = 108;
  const CASCADE_DIAGONAL = 18; // px right shift per step

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Subtle red radial pulse in background */}
      <AbsoluteFill style={{ pointerEvents: "none" }}>
        <div
          style={{
            position: "absolute",
            left: -200,
            top: -200,
            width: 1000,
            height: 1000,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${COLORS.neonRed}${Math.round(bgPulse * 255).toString(16).padStart(2, "0")} 0%, transparent 70%)`,
          }}
        />
      </AbsoluteFill>

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonRed, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonOrange, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonGreen, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 55,
          left: 0,
          right: 0,
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(titleAppear, [0, 1], [-20, 0])}px)`,
        }}
      >
        <span
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            color: COLORS.neonRed,
            textShadow: `0 0 30px ${COLORS.neonRed}80, 0 0 60px ${COLORS.neonRed}40`,
          }}
        >
          The Problem
        </span>
      </div>

      {/* Problem cascade — left side, 3D tilted */}
      <div
        style={{
          position: "absolute",
          top: CASCADE_TOP_START,
          left: CASCADE_LEFT,
          width: 780,
          perspective: "1000px",
        }}
      >
        <div style={{ transform: `rotateY(5deg) translateZ(${Math.sin(frame * 0.01) * 3}px)`, transformOrigin: "left center" }}>
          {PROBLEMS.map((problem, i) => {
            const s = problemSprings[i];
            const opacity = interpolate(s, [0, 1], [0, 1]);
            const translateX = interpolate(s, [0, 1], [-60, 0]);
            const scale = interpolate(s, [0, 1], [0.92, 1]);

            const diagonalOffset = i * CASCADE_DIAGONAL;

            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: i * CASCADE_STEP_H,
                  left: diagonalOffset,
                  width: 700,
                  opacity,
                  transform: `translateX(${translateX}px) scale(${scale})`,
                }}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.neonRed}18, ${COLORS.bgCard})`,
                    border: `1px solid ${COLORS.neonRed}40`,
                    borderLeft: `4px solid ${COLORS.neonRed}`,
                    borderRadius: 12,
                    padding: "18px 28px",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: `0 0 20px ${COLORS.neonRed}20, 0 4px 20px rgba(0,0,0,0.4)`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      color: COLORS.neonRed,
                      fontFamily: FONTS.mono,
                      fontWeight: 700,
                      opacity: 0.7,
                      minWidth: 28,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    style={{
                      fontSize: 19,
                      fontFamily: FONTS.body,
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                    }}
                  >
                    {problem}
                  </span>
                </div>
              </div>
            );
          })}

          {/* SVG connecting lines between cards */}
          <svg
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            width={760}
            height={PROBLEMS.length * CASCADE_STEP_H}
          >
            {PROBLEMS.slice(0, -1).map((_, i) => {
              const lineOpacity = interpolate(
                problemSprings[i + 1],
                [0, 1],
                [0, 1],
              );
              const x1 = CASCADE_DIAGONAL * i + 50;
              const y1 = i * CASCADE_STEP_H + 57;
              const x2 = CASCADE_DIAGONAL * (i + 1) + 50;
              const y2 = (i + 1) * CASCADE_STEP_H + 1;
              const dashOffset = -(frame * 3) % 16;
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={COLORS.neonRed}
                  strokeWidth={2}
                  strokeOpacity={lineOpacity * 0.6}
                  strokeDasharray="8 8"
                  strokeDashoffset={dashOffset}
                />
              );
            })}
          </svg>
        </div>
      </div>

      {/* Right side: "The Gap" section slides in from right */}
      <div
        style={{
          position: "absolute",
          top: 160,
          right: 60,
          width: 500,
          opacity: interpolate(gapAppear, [0, 1], [0, 1]),
          transform: `translateX(${interpolate(gapAppear, [0, 1], [80, 0])}px)`,
        }}
      >
        {/* Gap title */}
        <div
          style={{
            fontSize: 32,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.textPrimary,
            marginBottom: 32,
            borderBottom: `2px solid ${COLORS.neonOrange}60`,
            paddingBottom: 16,
          }}
        >
          The Gap
        </div>

        {/* Competitor line */}
        <div
          style={{
            opacity: interpolate(competitorAppear, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(competitorAppear, [0, 1], [16, 0])}px)`,
            marginBottom: 28,
          }}
        >
          <div
            style={{
              background: `${COLORS.neonOrange}15`,
              border: `1px solid ${COLORS.neonOrange}40`,
              borderRadius: 12,
              padding: "20px 24px",
              boxShadow: `0 0 20px ${COLORS.neonOrange}20`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontFamily: FONTS.mono,
                color: COLORS.neonOrange,
                textTransform: "uppercase",
                letterSpacing: 2,
                marginBottom: 8,
              }}
            >
              Competitors
            </div>
            <div
              style={{
                fontSize: 22,
                fontFamily: FONTS.heading,
                fontWeight: 700,
                color: COLORS.neonOrange,
                textShadow: `0 0 15px ${COLORS.neonOrange}60`,
              }}
            >
              $15–25/seat · 500-seat minimum
            </div>
          </div>
        </div>

        {/* SME opportunity */}
        <div
          style={{
            opacity: interpolate(smeAppear, [0, 1], [0, 1]),
            transform: `scale(${interpolate(smeAppear, [0, 1], [0.88, 1])}) translateY(${interpolate(smeAppear, [0, 1], [20, 0])}px)`,
          }}
        >
          <div
            style={{
              background: `${COLORS.neonGreen}12`,
              border: `2px solid ${COLORS.neonGreen}60`,
              borderRadius: 16,
              padding: "24px 28px",
              boxShadow: `0 0 30px ${COLORS.neonGreen}30, 0 0 60px ${COLORS.neonGreen}15`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontFamily: FONTS.mono,
                color: COLORS.neonGreen,
                textTransform: "uppercase",
                letterSpacing: 2,
                marginBottom: 10,
                opacity: 0.8,
              }}
            >
              The Opportunity
            </div>
            <div
              style={{
                fontSize: 26,
                fontFamily: FONTS.heading,
                fontWeight: 800,
                color: COLORS.neonGreen,
                lineHeight: 1.35,
                textShadow: `0 0 20px ${COLORS.neonGreen}80, 0 0 40px ${COLORS.neonGreen}40`,
              }}
            >
              63M+ SMEs in India
              <br />
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: COLORS.textSecondary,
                  textShadow: "none",
                }}
              >
                Nobody is building for them
              </span>
            </div>
          </div>
        </div>

        {/* Decorative vertical separator line */}
        <div
          style={{
            position: "absolute",
            left: -30,
            top: 60,
            bottom: 0,
            width: 1,
            background: `linear-gradient(to bottom, ${COLORS.neonOrange}60, transparent)`,
            opacity: interpolate(gapAppear, [0, 1], [0, 0.6]),
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
